import React, { useEffect, useState } from 'react';
import {
	Box,
	Typography,
	FormControlLabel,
	Checkbox,
	Grid,
	useTheme,
	Dialog,
	Button,
	CircularProgress,
	useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/system';
import { useSelector, useDispatch } from 'react-redux';
import { availableLanguagesWithInfo } from 'common/utils/languageUtils';
import { useTranslation } from 'services/localization';
import { getLanguageName } from 'common/locales/utils';
import * as NotificationActions from 'actions/NotificationActions';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { api } from 'common/frontend/api';
import { Languages } from 'common/types';
import { sleep } from 'common/utils/async';

interface Props {
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
}
const LanguageSelectModal = (props: Props) => {
	const { isOpen, setIsOpen } = props;
	const { t } = useTranslation();
	const theme = useTheme();
	const dispatch = useDispatch();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const languages = useSelector(ShopSelectors.activeShopLanguages);
	const shopPublicInfo = useSelector(ShopSelectors.activeShopPublicInfo);

	const [selectedLanguages, setSelectedLanguages] = useState<Languages[]>(languages ?? []);
	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		if (!isOpen) {
			setSelectedLanguages(languages);
		}
	}, [languages, isOpen]);

	const handleModalClose = () => {
		setIsOpen(false);
	};

	const isLanguageSelected = (language: Languages): boolean =>
		selectedLanguages.some((savedLanguage) => savedLanguage === language);

	const handleLanguageSelect = (language: Languages) => {
		isLanguageSelected(language)
			? setSelectedLanguages(
					selectedLanguages.filter((savedLanguage) => savedLanguage !== language),
			  )
			: setSelectedLanguages([...selectedLanguages, language]);
	};

	const handleUpdateLanguages = async () => {
		if (!shopPublicInfo) return;
		if (!selectedLanguages.length) return;
		setLoading(true);
		try {
			await Promise.all([
				api().shops.doc(shopPublicInfo.shopId).public.update({ languages: selectedLanguages }),
				sleep(1000),
			]);
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: 'Unable to update languages',
					variant: 'error',
				}),
			);
		}
		setLoading(false);
		setIsOpen(false);
	};

	return (
		<Dialog open={isOpen} onClose={handleModalClose} fullScreen={isMobile} maxWidth="md">
			<DialogContent>
				<Box mb={4}>
					<Typography sx={{ marginBottom: theme.spacing(0.5) }} variant="h5">
						{t('account.languages.languageSettings.languageModalTitle', 'Manage languages')}
					</Typography>
				</Box>
				<Grid mb={2} container item xs={12}>
					{availableLanguagesWithInfo.map((langObj) => {
						return (
							<Grid key={langObj.lang} item xs={12} md={6}>
								<FormControlLabel
									control={
										<Checkbox
											checked={isLanguageSelected(langObj.lang)}
											onChange={() => handleLanguageSelect(langObj.lang)}
											value={langObj.lang}
											color="primary"
											disabled={langObj.lang === shopPublicInfo?.defaultLanguage}
										/>
									}
									label={
										<>
											<Typography variant="body2" display="inline">
												{getLanguageName(langObj.lang, t)} - {langObj.lang.toUpperCase()}{' '}
												{langObj.lang === shopPublicInfo?.defaultLanguage &&
													`(${t('account.languages.main')})`}
												{langObj.info === 'rentle-free' ? '(Rentle Free)' : langObj.info ? '*' : ''}
											</Typography>
										</>
									}
								/>
							</Grid>
						);
					})}
					<Grid item xs={12} sx={{ mt: 2 }}>
						*{' '}
						{t(
							'account.languages.languageSettings.onlyCheckinOnline',
							'Only in check-in app & online store',
						)}
					</Grid>
				</Grid>
			</DialogContent>
			{isMobile ? (
				<MobileDialogActions mt={3}>
					<DialogActionsWrapper>
						<Button onClick={handleModalClose}>{t('common:actions.cancel')}</Button>
						<Button variant="contained" onClick={handleUpdateLanguages} disabled={loading}>
							{!loading ? t('common:actions.update') : <CircularProgress />}
						</Button>
					</DialogActionsWrapper>
				</MobileDialogActions>
			) : (
				<DialogActionsWrapper>
					<Button
						sx={{
							color: theme.palette.colors.tundra.main,
						}}
						onClick={handleModalClose}
					>
						{t('common:actions.cancel')}
					</Button>
					<Button variant="contained" onClick={handleUpdateLanguages} disabled={loading}>
						{!loading ? t('common:actions.save') : <CircularProgress size={24} />}
					</Button>
				</DialogActionsWrapper>
			)}
		</Dialog>
	);
};

const MobileDialogActions = styled(Box)(({ theme }) => ({
	position: 'fixed',
	bottom: 0,
	background: theme.palette.background.paper,
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	boxShadow: '0 -2px 8px 0 rgba(0, 0, 0, 0.11)',
	marginTop: theme.spacing(4),
}));

const DialogActionsWrapper = styled(Box)(({ theme }) => ({
	display: 'flex',
	justifyContent: 'flex-end',
	padding: theme.spacing(0, 6, 6, 6),
	width: '100%',
	[theme.breakpoints.down('md')]: {
		padding: theme.spacing(2),
	},
}));

const DialogContent = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	padding: theme.spacing(6),
	[theme.breakpoints.down('md')]: {
		padding: theme.spacing(6, 4, 20, 4),
	},
}));

export default LanguageSelectModal;
